import { useQuery } from "@tanstack/react-query";
import { ActionCard, DataList, StatusPill, type DataListColumn } from "@ozpanel/kit";
import { Section, StateView, useAuth } from "@ozpanel/ui";

// The two halves of a release that a rollout does not finish by itself, beside the rollout.
//
// Both endpoints existed with no console (CLAUDE.md rule 8): the embedded-binary reconcile
// (POST /cluster/artifacts/reconcile) was a curl, and the coordinated schema migrate
// (POST /cluster/updates/migrate) was reachable only from scripts/deploy-node.sh.

interface Drift {
  component: string;
  binary: string;
  dst_path: string;
  expected_sha256: string;
  actual_sha256?: string;
  state: string;
  message?: string;
}

const tone = (s: string) => (s === "ok" ? "ok" : s === "unavailable" ? "neutral" : "bad");

/** Managed binaries on THIS node against the release this panel carries, and the on-demand reconcile. */
export function ManagedBinaries() {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["cluster-artifacts-drift"],
    queryFn: () =>
      client.apiFetch<{ artifacts: Drift[] | null; drifted: number; would_restart?: unknown; note?: string }>(
        "/cluster/artifacts/drift",
      ),
    refetchInterval: 60000,
  });
  const cols: DataListColumn<Drift>[] = [
    { key: "component", label: "Component", sortable: true, value: (d) => d.component },
    { key: "binary", label: "Binary", value: (d) => d.dst_path, render: (d) => <span className="font-mono text-[12px]">{d.dst_path}</span> },
    {
      key: "state",
      label: "On disk",
      sortable: true,
      value: (d) => d.state,
      render: (d) => <StatusPill tone={tone(d.state)}>{d.state}</StatusPill>,
    },
    { key: "message", label: "Why", value: (d) => d.message ?? "" },
  ];
  const drifted = q.data?.drifted ?? 0;
  return (
    <Section
      title="Managed binaries on this node"
      hint={q.data ? (drifted ? `${drifted} drifted` : "all match the release") : undefined}
      bodyClassName="p-0"
    >
      <StateView loading={q.isLoading} error={q.error} errorContext="the managed binaries">
        <div className="p-4">
          <DataList
            items={q.data?.artifacts ?? []}
            columns={cols}
            getRowKey={(d) => `${d.component}/${d.binary}`}
            emptyMessage="This build carries no managed binaries."
          />
        </div>
        {q.data?.note ? (
          <div className="border-t border-line px-4 py-2.5 text-[12px] leading-relaxed text-muted">{q.data.note}</div>
        ) : null}
      </StateView>
      <div className="space-y-3 border-t border-line p-4">
        <ActionCard
          title="Preview the reconcile"
          description="Lists what would be re-placed and which daemons would restart. Changes nothing."
          actionLabel="Preview"
          run={() => client.apiFetch("/cluster/artifacts/reconcile?dry_run=1", { method: "POST" })}
        />
        <ActionCard
          title="Re-place drifted binaries and restart their daemons"
          description="The startup rollout fold-in, now: every drifted binary on THIS node is replaced by the copy embedded in the running release, and each daemon whose binary changed is restarted onto it (a new binary is not in force until its daemon restarts). Nothing on other nodes is touched."
          actionLabel="Reconcile now"
          danger="high"
          unavailable={q.data && drifted === 0 ? "Every managed binary on this node already matches the release, so there is nothing to re-place." : undefined}
          confirm={{
            title: "Re-place drifted binaries on this node?",
            body: "Each daemon whose binary is replaced restarts, so its service drops its connections for a moment (Caddy: every site on this node; tinc: this node's mesh links). Run the preview first to see which ones.",
            confirmLabel: "Reconcile and restart",
          }}
          run={() => client.apiFetch("/cluster/artifacts/reconcile?dry_run=0", { method: "POST" })}
          invalidates={[["cluster-artifacts-drift"]]}
        />
      </div>
    </Section>
  );
}

/** The coordinated schema migration, which must run on the workload primary. */
export function SchemaMigrate() {
  const { client } = useAuth();
  const arb = useQuery({
    queryKey: ["cluster-arbiter"],
    queryFn: () =>
      client.apiFetch<{ arbiter_home: string; declared_primary: string; this_node: string; belongs_here: boolean }>("/cluster/arbiter"),
    refetchInterval: 60000,
  });
  const here = arb.data?.this_node;
  const primary = arb.data?.declared_primary;
  return (
    <Section title="Schema migrations">
      <ActionCard
        title="Apply pending migrations across the cluster"
        description="Serialises every node's Galera appliers, applies any pending schema migration from this release, then restores the appliers — the coordinated safe-DDL step a deploy runs after the binary rollout. The workload primary's gate also applies pending migrations by itself; this is the immediate form."
        actionLabel="Migrate"
        danger="high"
        preflight={[
          {
            ok: !!here && !!primary && here === primary,
            label: here && primary ? (here === primary ? `${here} is the workload primary` : `${here} is not the workload primary (${primary} is)`) : "the workload primary could not be read",
            detail: "Only the primary may drive it; elsewhere the server refuses and names the node to run it on.",
          },
        ]}
        confirm={{
          title: "Run the coordinated migration now?",
          body: "Replication on every node runs single-threaded until it finishes, so writes from other nodes apply more slowly for its duration. A migration already applied is skipped.",
          confirmLabel: "Migrate",
        }}
        run={() => client.apiFetch("/cluster/updates/migrate", { method: "POST" })}
        invalidates={[["cluster-updates"]]}
      />
    </Section>
  );
}
